import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Delete,
  Publish,
  CheckCircle,
  Cancel,
  Add,
  LocationOn,
  CalendarToday,
  AttachMoney,
  Person,
  Info,
  PhotoLibrary,
  People,
} from '@mui/icons-material';
import api from '../services/api';
import PhotoGallery from '../components/PhotoGallery';
import PhotoUploader from '../components/PhotoUploader';
import StaffScheduler from '../components/StaffScheduler';

const SaleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [saleDays, setSaleDays] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [staffAssignments, setStaffAssignments] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [openDayDialog, setOpenDayDialog] = useState(false);
  const [statusAction, setStatusAction] = useState('');

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Edit form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    county: '',
    start_date: '',
    end_date: '',
    setup_date: '',
    estimated_value: '',
    commission_rate: '',
    client_id: '',
    status: 'draft',
  });
  const [formErrors, setFormErrors] = useState({});

  // Sale day form
  const [dayFormData, setDayFormData] = useState({
    sale_date: '',
    start_time: '09:00',
    end_time: '17:00',
    notes: '',
  });

  useEffect(() => {
    fetchSaleDetails();
    fetchInventoryItems();
    fetchSaleDays();
    fetchPhotos();
    fetchStaffAssignments();
  }, [id]);

  const fetchSaleDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/sales/${id}`);
      setSale(response.data);
    } catch (error) {
      console.error('Error fetching sale details:', error);
      showSnackbar('Failed to fetch sale details', 'error');
      if (error.response?.status === 404) {
        navigate('/sales');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchInventoryItems = async () => {
    try {
      const response = await api.get(`/inventory/?sale_id=${id}`);
      setInventoryItems(response.data?.items || response.data || []);
    } catch (error) {
      console.error('Error fetching inventory items:', error);
    }
  };

  const fetchSaleDays = async () => {
    try {
      const response = await api.get(`/sales/${id}/days`);
      setSaleDays(response.data?.days || response.data || []);
    } catch (error) {
      console.error('Error fetching sale days:', error);
    }
  };

  const fetchPhotos = async () => {
    try {
      setPhotosLoading(true);
      const response = await api.get(`/sales/${id}/photos`);
      setPhotos(response.data?.photos || response.data || []);
    } catch (error) {
      console.error('Error fetching photos:', error);
      setPhotos([]);
    } finally {
      setPhotosLoading(false);
    }
  };

  const fetchStaffAssignments = async () => {
    try {
      const response = await api.get(`/sales/${id}/staff`);
      setStaffAssignments(response.data?.assignments || response.data || []);
    } catch (error) {
      console.error('Error fetching staff assignments:', error);
      setStaffAssignments([]);
    }
  };

  const handlePhotoDelete = async (photoId) => {
    try {
      await api.delete(`/sales/${id}/photos/${photoId}`);
      showSnackbar('Photo deleted successfully', 'success');
      fetchPhotos();
    } catch (error) {
      console.error('Error deleting photo:', error);
      showSnackbar('Failed to delete photo', 'error');
    }
  };

  const handlePhotoUpdate = async (photoId, updates) => {
    try {
      await api.put(`/sales/${id}/photos/${photoId}`, updates);
      showSnackbar('Photo updated successfully', 'success');
      fetchPhotos();
    } catch (error) {
      console.error('Error updating photo:', error);
      showSnackbar('Failed to update photo', 'error');
    }
  };

  const handleSetPrimaryPhoto = async (photoId) => {
    try {
      await api.put(`/sales/${id}/photos/${photoId}/primary`);
      showSnackbar('Primary photo set successfully', 'success');
      fetchPhotos();
    } catch (error) {
      console.error('Error setting primary photo:', error);
      showSnackbar('Failed to set primary photo', 'error');
    }
  };

  const handleUploadComplete = () => {
    showSnackbar('Photos uploaded successfully', 'success');
    fetchPhotos();
  };

  const handleOpenEditDialog = () => {
    if (!sale) return;
    setFormData({
      name: sale.name || '',
      description: sale.description || '',
      address: sale.address || '',
      city: sale.city || '',
      state: sale.state || '',
      zip_code: sale.zip_code || '',
      county: sale.county || '',
      start_date: sale.start_date ? sale.start_date.split('T')[0] : '',
      end_date: sale.end_date ? sale.end_date.split('T')[0] : '',
      setup_date: sale.setup_date ? sale.setup_date.split('T')[0] : '',
      estimated_value: sale.estimated_value || '',
      commission_rate: sale.commission_rate || '',
      client_id: sale.client_id || '',
      status: sale.status || 'draft',
    });
    setFormErrors({});
    setOpenEditDialog(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name?.trim()) errors.name = 'Name is required';
    if (!formData.address?.trim()) errors.address = 'Address is required';
    if (!formData.city?.trim()) errors.city = 'City is required';
    if (!formData.state?.trim()) errors.state = 'State is required';
    if (!formData.zip_code?.trim()) errors.zip_code = 'ZIP code is required';
    if (formData.commission_rate && (formData.commission_rate < 0 || formData.commission_rate > 100)) {
      errors.commission_rate = 'Commission rate must be between 0 and 100';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitEdit = async () => {
    if (!validateForm()) return;

    try {
      const submitData = { ...formData };

      if (!submitData.client_id) submitData.client_id = null;
      if (!submitData.estimated_value) submitData.estimated_value = null;
      if (!submitData.commission_rate) submitData.commission_rate = null;
      if (!submitData.setup_date) submitData.setup_date = null;

      await api.put(`/sales/${id}`, submitData);
      showSnackbar('Sale updated successfully', 'success');
      setOpenEditDialog(false);
      fetchSaleDetails();
    } catch (error) {
      console.error('Error updating sale:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Failed to update sale';
      showSnackbar(errorMsg, 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/sales/${id}`);
      showSnackbar('Sale deleted successfully', 'success');
      navigate('/sales');
    } catch (error) {
      console.error('Error deleting sale:', error);
      showSnackbar('Failed to delete sale', 'error');
    }
  };

  const handleStatusChange = async (action) => {
    setStatusAction(action);
    setOpenStatusDialog(true);
  };

  const confirmStatusChange = async () => {
    try {
      let endpoint = '';
      let method = 'post';

      switch (statusAction) {
        case 'publish':
          endpoint = `/sales/${id}/publish`;
          break;
        case 'complete':
          endpoint = `/sales/${id}`;
          method = 'put';
          await api.put(`/sales/${id}`, { ...sale, status: 'completed' });
          break;
        case 'cancel':
          endpoint = `/sales/${id}`;
          method = 'put';
          await api.put(`/sales/${id}`, { ...sale, status: 'cancelled' });
          break;
        default:
          return;
      }

      if (statusAction === 'publish') {
        await api.post(endpoint);
      }

      showSnackbar(`Sale ${statusAction}ed successfully`, 'success');
      setOpenStatusDialog(false);
      fetchSaleDetails();
    } catch (error) {
      console.error(`Error ${statusAction}ing sale:`, error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || `Failed to ${statusAction} sale`;
      showSnackbar(errorMsg, 'error');
    }
  };

  const handleAddSaleDay = async () => {
    try {
      await api.post(`/sales/${id}/days`, dayFormData);
      showSnackbar('Sale day added successfully', 'success');
      setOpenDayDialog(false);
      setDayFormData({
        sale_date: '',
        start_time: '09:00',
        end_time: '17:00',
        notes: '',
      });
      fetchSaleDays();
    } catch (error) {
      console.error('Error adding sale day:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Failed to add sale day';
      showSnackbar(errorMsg, 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      scheduled: 'info',
      published: 'primary',
      in_progress: 'warning',
      completed: 'success',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  const handleFormChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (formErrors[field]) {
      setFormErrors({ ...formErrors, [field]: '' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!sale) {
    return (
      <Box textAlign="center" py={6}>
        <Typography variant="h5" color="text.secondary">
          Sale not found
        </Typography>
        <Button onClick={() => navigate('/sales')} sx={{ mt: 2 }}>
          Back to Sales
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Box display="flex" alignItems="center" mb={2}>
          <IconButton onClick={() => navigate('/sales')} sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <Box flex={1}>
            <Typography variant="h4" fontWeight="bold">
              {sale.name}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Sale Details
            </Typography>
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            <Chip
              label={sale.status}
              color={getStatusColor(sale.status)}
              size="medium"
            />
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={handleOpenEditDialog}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={() => setOpenDeleteDialog(true)}
            >
              Delete
            </Button>
          </Box>
        </Box>

        {/* Status Action Buttons */}
        <Box display="flex" gap={1}>
          {sale.status === 'draft' && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Publish />}
              onClick={() => handleStatusChange('publish')}
            >
              Publish Sale
            </Button>
          )}
          {(sale.status === 'published' || sale.status === 'in_progress') && (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              onClick={() => handleStatusChange('complete')}
            >
              Mark Complete
            </Button>
          )}
          {sale.status !== 'completed' && sale.status !== 'cancelled' && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Cancel />}
              onClick={() => handleStatusChange('cancel')}
            >
              Cancel Sale
            </Button>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Details" />
          <Tab label="Sale Days" />
          <Tab label={`Inventory (${inventoryItems.length})`} />
          <Tab
            label={
              <Badge badgeContent={photos.length} color="primary">
                Photos
              </Badge>
            }
          />
          <Tab
            label={`Staff (${staffAssignments.length})`}
            icon={<People />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Main Details Card */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                  Sale Information
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {sale.description && (
                  <Box mb={3}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Description
                    </Typography>
                    <Typography variant="body1">{sale.description}</Typography>
                  </Box>
                )}

                <Box display="flex" flexDirection="column" gap={2}>
                  <Box display="flex" alignItems="start" gap={2}>
                    <LocationOn color="action" />
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Location
                      </Typography>
                      <Typography variant="body1">
                        {sale.address}
                        <br />
                        {sale.city}, {sale.state} {sale.zip_code}
                        {sale.county && <><br />County: {sale.county}</>}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider />

                  <Box display="flex" alignItems="start" gap={2}>
                    <CalendarToday color="action" />
                    <Box flex={1}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Sale Dates
                      </Typography>
                      <Grid container spacing={2}>
                        {sale.setup_date && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Setup Date
                            </Typography>
                            <Typography variant="body1">
                              {new Date(sale.setup_date).toLocaleDateString()}
                            </Typography>
                          </Grid>
                        )}
                        {sale.start_date && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Start Date
                            </Typography>
                            <Typography variant="body1">
                              {new Date(sale.start_date).toLocaleDateString()}
                            </Typography>
                          </Grid>
                        )}
                        {sale.end_date && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              End Date
                            </Typography>
                            <Typography variant="body1">
                              {new Date(sale.end_date).toLocaleDateString()}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </Box>

                  {(sale.estimated_value || sale.commission_rate) && (
                    <>
                      <Divider />
                      <Box display="flex" alignItems="start" gap={2}>
                        <AttachMoney color="action" />
                        <Box flex={1}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Financial Details
                          </Typography>
                          <Grid container spacing={2}>
                            {sale.estimated_value && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Estimated Value
                                </Typography>
                                <Typography variant="body1">
                                  ${Number(sale.estimated_value).toLocaleString()}
                                </Typography>
                              </Grid>
                            )}
                            {sale.commission_rate && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Commission Rate
                                </Typography>
                                <Typography variant="body1">
                                  {sale.commission_rate}%
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      </Box>
                    </>
                  )}

                  {sale.client_id && (
                    <>
                      <Divider />
                      <Box display="flex" alignItems="start" gap={2}>
                        <Person color="action" />
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Client ID
                          </Typography>
                          <Typography variant="body1">{sale.client_id}</Typography>
                        </Box>
                      </Box>
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Stats Card */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                  Quick Stats
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box display="flex" flexDirection="column" gap={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={sale.status}
                      color={getStatusColor(sale.status)}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Inventory Items
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {inventoryItems.length}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Sale Days
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {saleDays.length}
                    </Typography>
                  </Box>

                  {sale.created_at && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Created
                      </Typography>
                      <Typography variant="body1">
                        {new Date(sale.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" fontWeight="bold">
              Sale Days
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setOpenDayDialog(true)}
            >
              Add Sale Day
            </Button>
          </Box>

          {saleDays.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Start Time</TableCell>
                    <TableCell>End Time</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {saleDays.map((day, index) => (
                    <TableRow key={day.id || index}>
                      <TableCell>
                        {new Date(day.sale_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{day.start_time || 'N/A'}</TableCell>
                      <TableCell>{day.end_time || 'N/A'}</TableCell>
                      <TableCell>{day.notes || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <CalendarToday sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No sale days scheduled
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>
                  Add sale days to specify when the sale will be open
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setOpenDayDialog(true)}
                >
                  Add First Day
                </Button>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" fontWeight="bold">
              Inventory Items
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate('/inventory')}
            >
              View All Inventory
            </Button>
          </Box>

          {inventoryItems.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Item Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inventoryItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.name || 'Untitled Item'}</TableCell>
                      <TableCell>{item.category || 'N/A'}</TableCell>
                      <TableCell>
                        {item.price ? `$${Number(item.price).toLocaleString()}` : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Chip label={item.status || 'available'} size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <Info sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No inventory items
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>
                  Add inventory items to this sale from the inventory page
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/inventory')}
                >
                  Go to Inventory
                </Button>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {activeTab === 3 && (
        <Box>
          <Typography variant="h6" fontWeight="bold" mb={3}>
            Photo Gallery
          </Typography>

          {/* Photo Uploader */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" mb={2}>
                Upload Photos
              </Typography>
              <PhotoUploader
                saleId={id}
                onUploadComplete={handleUploadComplete}
              />
            </CardContent>
          </Card>

          {/* Photo Gallery */}
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>
                Gallery ({photos.length})
              </Typography>
              <PhotoGallery
                photos={photos}
                onDelete={handlePhotoDelete}
                onUpdate={handlePhotoUpdate}
                onSetPrimary={handleSetPrimaryPhoto}
                loading={photosLoading}
              />
            </CardContent>
          </Card>
        </Box>
      )}

      {activeTab === 4 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" fontWeight="bold">
              Staff Assignments
            </Typography>
          </Box>
          <StaffScheduler saleId={id} />
        </Box>
      )}

      {/* Edit Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Sale</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Sale Name"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.address}
                  onChange={(e) => handleFormChange('address', e.target.value)}
                  error={!!formErrors.address}
                  helperText={formErrors.address}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.city}
                  onChange={(e) => handleFormChange('city', e.target.value)}
                  error={!!formErrors.city}
                  helperText={formErrors.city}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="State"
                  value={formData.state}
                  onChange={(e) => handleFormChange('state', e.target.value)}
                  error={!!formErrors.state}
                  helperText={formErrors.state}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  value={formData.zip_code}
                  onChange={(e) => handleFormChange('zip_code', e.target.value)}
                  error={!!formErrors.zip_code}
                  helperText={formErrors.zip_code}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="County"
                  value={formData.county}
                  onChange={(e) => handleFormChange('county', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Setup Date"
                  value={formData.setup_date}
                  onChange={(e) => handleFormChange('setup_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date"
                  value={formData.start_date}
                  onChange={(e) => handleFormChange('start_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="End Date"
                  value={formData.end_date}
                  onChange={(e) => handleFormChange('end_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Estimated Value"
                  value={formData.estimated_value}
                  onChange={(e) => handleFormChange('estimated_value', e.target.value)}
                  InputProps={{ startAdornment: '$' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Commission Rate"
                  value={formData.commission_rate}
                  onChange={(e) => handleFormChange('commission_rate', e.target.value)}
                  error={!!formErrors.commission_rate}
                  helperText={formErrors.commission_rate}
                  InputProps={{ endAdornment: '%' }}
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmitEdit} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete Sale</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{sale.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Change Confirmation Dialog */}
      <Dialog open={openStatusDialog} onClose={() => setOpenStatusDialog(false)}>
        <DialogTitle>
          {statusAction === 'publish' && 'Publish Sale'}
          {statusAction === 'complete' && 'Complete Sale'}
          {statusAction === 'cancel' && 'Cancel Sale'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {statusAction === 'publish' && `Are you sure you want to publish "${sale.name}"?`}
            {statusAction === 'complete' && `Are you sure you want to mark "${sale.name}" as completed?`}
            {statusAction === 'cancel' && `Are you sure you want to cancel "${sale.name}"?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenStatusDialog(false)}>Cancel</Button>
          <Button onClick={confirmStatusChange} variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Sale Day Dialog */}
      <Dialog open={openDayDialog} onClose={() => setOpenDayDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Sale Day</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="date"
                  label="Sale Date"
                  value={dayFormData.sale_date}
                  onChange={(e) => setDayFormData({ ...dayFormData, sale_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="time"
                  label="Start Time"
                  value={dayFormData.start_time}
                  onChange={(e) => setDayFormData({ ...dayFormData, start_time: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="time"
                  label="End Time"
                  value={dayFormData.end_time}
                  onChange={(e) => setDayFormData({ ...dayFormData, end_time: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={dayFormData.notes}
                  onChange={(e) => setDayFormData({ ...dayFormData, notes: e.target.value })}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDayDialog(false)}>Cancel</Button>
          <Button onClick={handleAddSaleDay} variant="contained">
            Add Day
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SaleDetail;
